import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AddTableDialog from "admin/components/AddTableDialog";
import DatasetAccessBadge from "admin/components/DatasetAccessBadge";
import DatasetUsersDialog from "admin/components/DatasetUsersDialog";
import useAdminDatasets, { ADMIN_DATASETS_PAGE_SIZE } from "admin/hooks/useAdminDatasets";
import { format, parseISO } from "date-fns";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  ExternalLink,
  FolderPlus,
  Info,
  Loader2,
  MoreHorizontal,
  Search,
  TableProperties,
  Users,
  X,
} from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Input } from "~/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import useToast from "~/components/ui/toast";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "~/components/ui/tooltip";
import useUserPermission from "~/hooks/useUserPermission";
import { cn } from "~/lib/utils";
import { nextgenAdminDatasetDetailPath } from "~/pages/manifold-nextgen/paths";
import { ROUTE_ADMIN, ROUTE_DATASETS } from "~/router/constants";
import { ApiError, apiMutate } from "~/utils/apiClient";

const { VITE_CATALOG_API } = import.meta.env;

function formatApiError(message: string): string {
  try {
    const parsed = JSON.parse(message);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return message;
  }
}

const formatDate = (dateString: string) => format(parseISO(dateString), "MM/dd/yy h:mmaaa");

const CopyIdButton = ({ id }: { id: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(id).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-6 w-6 shrink-0"
      onClick={handleCopy}
      aria-label="Copy ID"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  );
};

type SortColumn = "id" | "title" | "createdBy" | "updated_at";
type SortDirection = "asc" | "desc";

// Columns the admin endpoint can sort server-side, mapped to its `sort` field names.
// Columns absent from this map (id, createdBy) are not sortable in server-side mode.
type ServerSortField = "title" | "updatedAt";
const SERVER_SORT_FIELD: Partial<Record<SortColumn, ServerSortField>> = {
  title: "title",
  updated_at: "updatedAt",
};

const getSortIcon = (column: SortColumn, activeColumn: SortColumn, direction: SortDirection) => {
  if (activeColumn !== column) {
    return <ArrowUpDown className="h-4 w-4 text-muted-foreground/50" />;
  }
  return direction === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
};

// Nova's content column is narrower than the legacy admin app's, so its columns are tighter
// to keep every column (including Actions) visible without horizontal scrolling.
const COLUMN_WIDTHS = {
  legacy: {
    id: "w-[280px]",
    access: "w-[120px]",
    name: "w-[240px]",
    createdBy: "w-[160px]",
    updated: "w-[160px]",
    actions: "w-[80px]",
  },
  nextGen: {
    id: "w-[250px]",
    access: "w-[100px]",
    name: "w-[180px]",
    createdBy: "w-[120px]",
    updated: "w-[145px]",
    actions: "w-[56px]",
  },
};

const DatasetTable = ({ isNextGen = false }: { isNextGen?: boolean }) => {
  const navigate = useNavigate();
  const columnWidths = isNextGen ? COLUMN_WIDTHS.nextGen : COLUMN_WIDTHS.legacy;
  const canViewDetail = useUserPermission("admin:catalog-dataset");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState<SortColumn>("title");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [offset, setOffset] = useState(0);
  const [serverSort, setServerSort] = useState<{ field: ServerSortField; dir: SortDirection }>({
    field: "title",
    dir: "asc",
  });
  const serverSortParam = `${serverSort.dir === "desc" ? "-" : ""}${serverSort.field}`;
  const {
    items: datasets,
    metadata,
    isLoading,
    error,
  } = useAdminDatasets({ offset, limit: ADMIN_DATASETS_PAGE_SIZE, sort: serverSortParam });

  // More datasets exist than fit in a single page: hand sorting + paging to the
  // server. Client-side search then only covers the rows on the current page.
  const totalCount = metadata?.total_count ?? 0;
  const isServerMode = totalCount > ADMIN_DATASETS_PAGE_SIZE;
  const isColumnSortable = (column: SortColumn) =>
    !isServerMode || SERVER_SORT_FIELD[column] !== undefined;
  const [addTableDatasetId, setAddTableDatasetId] = useState<string | null>(null);
  const [addFilePrefixDatasetId, setAddFilePrefixDatasetId] = useState<string | null>(null);
  const [viewUsersDatasetId, setViewUsersDatasetId] = useState<string | null>(null);
  const [filePrefix, setFilePrefix] = useState("");
  const [isAddingFilePrefix, setIsAddingFilePrefix] = useState(false);
  const [addFilePrefixError, setAddFilePrefixError] = useState<string | null>(null);
  const { showSuccess } = useToast();

  const handleAddFilePrefix = async () => {
    if (!addFilePrefixDatasetId || !filePrefix.trim()) return;
    setIsAddingFilePrefix(true);
    setAddFilePrefixError(null);
    try {
      await apiMutate(
        `${VITE_CATALOG_API}/admin/datasets/${addFilePrefixDatasetId}/file-configs`,
        "POST",
        { prefix: filePrefix.trim() }
      );
      showSuccess("File prefix added successfully");
      setAddFilePrefixDatasetId(null);
      setFilePrefix("");
    } catch (err) {
      const detail =
        err instanceof ApiError ? formatApiError(err.message) : "An unexpected error occurred";
      setAddFilePrefixError(detail);
    } finally {
      setIsAddingFilePrefix(false);
    }
  };

  const handleCloseAddFilePrefix = () => {
    setAddFilePrefixDatasetId(null);
    setFilePrefix("");
    setAddFilePrefixError(null);
  };

  const handleSort = (column: SortColumn) => {
    if (isServerMode) {
      const field = SERVER_SORT_FIELD[column];
      if (!field) return; // id and createdBy are not server-sortable
      setServerSort((prev) =>
        prev.field === field
          ? { field, dir: prev.dir === "asc" ? "desc" : "asc" }
          : { field, dir: "asc" }
      );
      setOffset(0);
      return;
    }
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // The active sort indicator follows server sort when paginating, client sort otherwise.
  const activeSortColumn: SortColumn = isServerMode
    ? serverSort.field === "updatedAt"
      ? "updated_at"
      : "title"
    : sortColumn;
  const activeSortDirection = isServerMode ? serverSort.dir : sortDirection;

  const filteredDatasets = searchTerm
    ? datasets.filter((dataset) => {
        const term = searchTerm.toLowerCase();
        return (
          dataset.id.toLowerCase().includes(term) ||
          dataset.title.toLowerCase().includes(term) ||
          (dataset.createdBy ?? "").toLowerCase().includes(term)
        );
      })
    : datasets;

  // In server mode the page is already sorted by the backend; only re-sort client-side
  // when the full result set is loaded.
  const displayedDatasets = isServerMode
    ? filteredDatasets
    : [...filteredDatasets].sort((a, b) => {
        let cmp = 0;
        if (sortColumn === "title" || sortColumn === "id") {
          cmp = (a[sortColumn] ?? "").localeCompare(b[sortColumn] ?? "");
        } else if (sortColumn === "createdBy") {
          cmp = (a.createdBy ?? "").localeCompare(b.createdBy ?? "");
        } else {
          cmp = (a.updated_at ?? "").localeCompare(b.updated_at ?? "");
        }
        return sortDirection === "asc" ? cmp : -cmp;
      });

  const hasMore = metadata?.has_more ?? false;
  const currentPage = Math.floor(offset / ADMIN_DATASETS_PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(totalCount / ADMIN_DATASETS_PAGE_SIZE));

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by ID, name, or creator..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-10 h-9 border border-border bg-white"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm("")}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {isServerMode && (
          <p className="mt-1.5 text-xs text-muted-foreground">
            Search applies to the current page only ({totalCount} datasets total). Navigate to other
            pages to browse all datasets.
          </p>
        )}
      </div>
      <div className="flex-1 rounded-lg border overflow-auto min-h-0">
        <Table className={cn(isNextGen && "table-fixed")}>
          <TableHeader>
            <TableRow>
              <TableHead
                className={cn(
                  columnWidths.id,
                  isColumnSortable("id") && "cursor-pointer select-none"
                )}
                onClick={isColumnSortable("id") ? () => handleSort("id") : undefined}
              >
                <div className="flex items-center gap-1">
                  ID
                  {isColumnSortable("id") &&
                    getSortIcon("id", activeSortColumn, activeSortDirection)}
                </div>
              </TableHead>
              <TableHead className={columnWidths.access}>Access</TableHead>
              <TableHead
                className={cn(
                  columnWidths.name,
                  isColumnSortable("title") && "cursor-pointer select-none"
                )}
                onClick={isColumnSortable("title") ? () => handleSort("title") : undefined}
              >
                <div className="flex items-center gap-1">
                  Name
                  {isColumnSortable("title") &&
                    getSortIcon("title", activeSortColumn, activeSortDirection)}
                </div>
              </TableHead>
              <TableHead
                className={cn(
                  columnWidths.createdBy,
                  isColumnSortable("createdBy") && "cursor-pointer select-none"
                )}
                onClick={isColumnSortable("createdBy") ? () => handleSort("createdBy") : undefined}
              >
                <div className="flex items-center gap-1">
                  Created By
                  {isColumnSortable("createdBy") &&
                    getSortIcon("createdBy", activeSortColumn, activeSortDirection)}
                </div>
              </TableHead>
              <TableHead
                className={cn(
                  columnWidths.updated,
                  isColumnSortable("updated_at") && "cursor-pointer select-none"
                )}
                onClick={
                  isColumnSortable("updated_at") ? () => handleSort("updated_at") : undefined
                }
              >
                <div className="flex items-center gap-1">
                  Last Updated
                  {isColumnSortable("updated_at") &&
                    getSortIcon("updated_at", activeSortColumn, activeSortDirection)}
                </div>
              </TableHead>
              <TableHead className={columnWidths.actions}>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {error ? (
              <TableRow className="pointer-events-none">
                <TableCell colSpan={6} className="text-center text-destructive py-8">
                  Error loading datasets
                </TableCell>
              </TableRow>
            ) : isLoading ? (
              <TableRow className="pointer-events-none">
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Loading datasets...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : displayedDatasets.length === 0 ? (
              <TableRow className="pointer-events-none">
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  {searchTerm ? `No datasets matching "${searchTerm}"` : "No datasets found"}
                </TableCell>
              </TableRow>
            ) : (
              displayedDatasets.map((dataset) => (
                <TableRow key={dataset.id}>
                  <TableCell className={cn("font-mono", isNextGen ? "text-xs" : "text-sm")}>
                    <div className="flex items-center gap-1">
                      <span>{dataset.id}</span>
                      <CopyIdButton id={dataset.id} />
                    </div>
                  </TableCell>
                  <TableCell>
                    <DatasetAccessBadge accessLevel={dataset.access_level} />
                  </TableCell>
                  <TableCell className="font-semibold">
                    <div className="flex items-center gap-1.5">
                      <span className={cn(isNextGen && "truncate")} title={dataset.title}>
                        {dataset.title}
                      </span>
                      {dataset.description && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-4 w-4 shrink-0 text-muted-foreground" />
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-[300px]">
                              <p>{dataset.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </TableCell>
                  <TableCell
                    className={cn(isNextGen && "truncate")}
                    title={dataset.createdBy ?? undefined}
                  >
                    {dataset.createdBy ?? "—"}
                  </TableCell>
                  <TableCell>{dataset.updated_at ? formatDate(dataset.updated_at) : "—"}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground"
                          aria-label="Dataset actions"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => setAddTableDatasetId(dataset.id)}>
                          <TableProperties />
                          Add Table
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => setAddFilePrefixDatasetId(dataset.id)}>
                          <FolderPlus />
                          Add File Prefix
                        </DropdownMenuItem>
                        {canViewDetail && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onSelect={() =>
                                navigate(
                                  isNextGen
                                    ? nextgenAdminDatasetDetailPath(dataset.id)
                                    : `${ROUTE_ADMIN}/${ROUTE_DATASETS}/${dataset.id}`
                                )
                              }
                            >
                              <ExternalLink />
                              Sync to DAC
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => setViewUsersDatasetId(dataset.id)}>
                              <Users />
                              View Users
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {isServerMode && (
        <div className="mt-4 flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Page {currentPage} of {totalPages} ({totalCount} datasets)
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => setOffset((prev) => Math.max(0, prev - ADMIN_DATASETS_PAGE_SIZE))}
              disabled={offset === 0}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => setOffset((prev) => prev + ADMIN_DATASETS_PAGE_SIZE)}
              disabled={!hasMore}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <AddTableDialog
        datasetId={addTableDatasetId}
        datasetTitle={datasets.find((d) => d.id === addTableDatasetId)?.title}
        onClose={() => setAddTableDatasetId(null)}
      />

      <DatasetUsersDialog
        datasetId={viewUsersDatasetId}
        datasetTitle={datasets.find((d) => d.id === viewUsersDatasetId)?.title}
        onClose={() => setViewUsersDatasetId(null)}
      />

      <Dialog open={addFilePrefixDatasetId !== null} onOpenChange={handleCloseAddFilePrefix}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add File Prefix</DialogTitle>
            <DialogDescription>
              <span className="font-semibold">
                {datasets.find((d) => d.id === addFilePrefixDatasetId)?.title}
              </span>
              <span className="font-mono text-xs ml-1.5">({addFilePrefixDatasetId})</span>
            </DialogDescription>
            <DialogDescription>
              Enter the file prefix to associate with this dataset. Prefixes in the platform storage
              bucket should use a relative path. Include the trailing slash.
            </DialogDescription>
          </DialogHeader>
          <Input
            placeholder="e.g. path/to/prefix/"
            value={filePrefix}
            onChange={(e) => setFilePrefix(e.target.value)}
          />
          {addFilePrefixError && (
            <p className="text-sm text-destructive whitespace-pre-wrap break-all">
              {addFilePrefixError}
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseAddFilePrefix}
              disabled={isAddingFilePrefix}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddFilePrefix}
              disabled={!filePrefix.trim() || isAddingFilePrefix}
            >
              {isAddingFilePrefix ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DatasetTable;
